import { AfterViewInit, Component, ElementRef, OnInit, OnDestroy, inject, signal, viewChild } from '@angular/core';
import { Router, RouterOutlet, ActivatedRoute, NavigationEnd, RouterLinkWithHref } from '@angular/router';
import { ProjectService } from '../../services/project.service';
import { ProjectMembershipService } from '../../services/project-membership.service';
import { SessionService } from '../../services/session.service';
import { DashboardService } from '../../../features/dashboard/services/dashboard.service';
import { ErrorHandlerService } from '../../services/error-handler.service';
import { ProjectsDataDto } from '../../models/dashboard.dto';
import { Subscription, filter } from 'rxjs';
import { UserMenuComponent } from '../user-menu/user-menu.component';
import { environment } from '../../../../environments/environment';
import { NotificationsPanelComponent } from '../notifications-panel/notifications-panel.component';
import { SidebarStateService } from '../../services/sidebar-state.service';
import { NotificationService } from '../../services/notification.service';
import { LucideAngularModule } from 'lucide-angular';
import { ProjectSearchComponent } from '../project-search/project-search.component';
import { SettingsModalComponent } from '../settings-modal/settings-modal.component';
import {
  CreateProjectPanelComponent,
  EditProjectData,
  ProjectData,
} from '../create-project-panel/create-project-panel.component';
import { ProjectPanelService } from '../../services/project-panel.service';
import { ProjectService as ProjectApiService } from '../../../features/dashboard/services/project.service';
import { ProjectRequestDto } from '../../../features/dashboard/models/project.dto';
import { SettingsModalTriggerService } from '../../services/settings-modal-trigger.service';
import { ToastService } from '../../toast/toast.service';
import { PageBreadcrumbService } from '../../services/page-breadcrumb.service';
import { ContentAreaService } from '../../services/content-area.service';
import { ProjectSelectorComponent } from '../project-selector/project-selector.component';
import { DashboardProjectSelectComponent } from '../dashboard-project-select/dashboard-project-select.component';

@Component({
  selector: 'app-project-dashboard-layout',
  imports: [
    RouterOutlet,
    UserMenuComponent,
    NotificationsPanelComponent,
    LucideAngularModule,
    ProjectSearchComponent,
    SettingsModalComponent,
    CreateProjectPanelComponent,
    RouterLinkWithHref,
    ProjectSelectorComponent,
    DashboardProjectSelectComponent,
  ],
  templateUrl: './project-dashboard-layout.component.html',
  styleUrl: './project-dashboard-layout.component.scss',
  host: {
    '(document:click)': 'onDocumentClick($event)',
    '(document:keydown.escape)': 'onDocumentEscape()',
  },
})
export class ProjectDashboardLayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly notificationService = inject(NotificationService);
  readonly pageBreadcrumbService = inject(PageBreadcrumbService);
  private readonly contentAreaService = inject(ContentAreaService);
  /** The page area below the header; overlays such as the expanded request panel cover it. */
  private readonly contentBody = viewChild.required<ElementRef<HTMLElement>>('contentBody');
  private releaseContentArea?: () => void;
  readonly session = inject(SessionService);
  notificationsOpen = false;
  public activeNav: 'dashboard' | 'sites' | 'requests' | 'members' = 'dashboard';
  public get sidebarCollapsed(): boolean {
    return this.sidebarStateService.collapsed();
  }
  /** Icon-only sidebar tooltip (collapsed nav items + the toggle button) — positioned via getBoundingClientRect on hover. */
  public readonly navTooltip = signal<{ text: string; top: number; left: number } | null>(null);
  public projectSwitcherOpen: boolean = false;
  public projects: ProjectsDataDto[] = [];
  public mobileNavOpen: boolean = false;
  public settingsModalOpen: boolean = false;
  public pendingSettingsSection: 'projects-general' | 'workspace-general' | null = null;
  public pendingSettingsProjectId?: string;
  /** Set when the modal is opening on an OAuth return, so the integration's own dialog opens with it. */
  public pendingSettingsIntegration: 'slack' | 'hubspot' | null = null;
  public pendingSettingsWorkspaceLinkId?: string;
  public createPanelOpen: boolean = false;
  public editingProject: EditProjectData | null = null;
  /** True once the dashboard fetch resolves at least once — avoids a flash of "locked" nav before we know the real project count. */
  public projectsLoaded: boolean = false;

  public readonly isStaging = environment.staging === true;

  private projectSub?: Subscription;
  private routerSub?: Subscription;
  private editProjectSub?: Subscription;
  private openCreateSub?: Subscription;
  private refreshSub?: Subscription;
  private settingsTriggerSub?: Subscription;

  constructor(
    private readonly projectService: ProjectService,
    private readonly projectMembershipService: ProjectMembershipService,
    private readonly dashboardService: DashboardService,
    private readonly errorHandler: ErrorHandlerService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly sidebarStateService: SidebarStateService,
    private readonly projectPanelService: ProjectPanelService,
    private readonly projectApiService: ProjectApiService,
    private readonly settingsModalTriggerService: SettingsModalTriggerService,
    private readonly toastService: ToastService,
  ) {}

  ngOnInit(): void {
    // Unread count drives the bell badge, so it loads eagerly; the full notifications
    // list is deferred until the bell is actually opened (see toggleNotifications()).
    this.notificationService.init();
    this.loadUserAndOrganizationData();

    // The active project is navigation context rather than identity, so it keeps its
    // observable; all this listener does is keep the membership fetch in step with it.
    this.projectSub = this.projectService.project$.subscribe((project) => {
      if (project.id) {
        this.projectMembershipService.loadForProject(project.id).subscribe();
      } else {
        this.projectMembershipService.clear();
      }
    });

    this.editProjectSub = this.projectPanelService.editProject$.subscribe((project) => {
      this.onEditProject(project);
    });

    this.openCreateSub = this.projectPanelService.openCreatePanel$.subscribe(() => {
      this.onCreateProject();
    });

    this.refreshSub = this.dashboardService.refreshDashboard$.subscribe(() => {
      this.loadUserAndOrganizationData();
    });

    this.settingsTriggerSub = this.settingsModalTriggerService.openIntegrationsRequest$.subscribe((request) => {
      if (!request) return;
      this.pendingSettingsSection = request.section ?? 'projects-general';
      this.pendingSettingsProjectId = request.projectId;
      // A failed round trip has no workspace to open a dialog on, so the row is as far as it goes.
      this.pendingSettingsIntegration = request.error ? null : (request.integration ?? null);
      this.pendingSettingsWorkspaceLinkId = request.workspaceLinkId;
      if (request.error) {
        const message = this.oauthReturnErrorMessage(request.integration, request.error);
        if (message) {
          this.toastService.error({ message });
        }
      } else if (request.integration === 'hubspot') {
        this.toastService.success({ message: 'HubSpot connected.' });
      }
      this.openSettingsModal();
      this.settingsModalTriggerService.clearRequest();
    });

    this.updateActiveNavFromRoute();

    this.routerSub = this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.updateActiveNavFromRoute();
      // The notifications overlay shouldn't linger over a freshly navigated page
      // (e.g. a deep-link from a notification, or a sidebar nav click).
      this.closeNotifications();
    });
  }

  private loadUserAndOrganizationData(): void {
    this.dashboardService.getDashboardData().subscribe({
      next: (response) => {
        this.projects = response.data.projects;

        // Pre-select the first project when none is active yet — or when the currently
        // active id is stale (e.g. left over from a different org, or a since-deleted
        // project) and doesn't belong to this org's project list — so routes like
        // /requests that gate their data loading on projectId don't hang empty, and a
        // single-project org always has its sole project selected by default.
        const activeProjectIsValid = this.projects.some((p) => p.id === this.projectService.projectId);
        if (!activeProjectIsValid && this.projects.length > 0) {
          this.selectProject(this.projects[0]);
        }

        this.projectsLoaded = true;
      },
      error: (error) => {
        const err = this.errorHandler.getUserFriendlyErrorMessage(error);
        console.error('Error loading user and organization data:', err);
        this.projectsLoaded = true;
      },
    });
  }

  ngAfterViewInit(): void {
    this.releaseContentArea = this.contentAreaService.observe(this.contentBody().nativeElement);
  }

  ngOnDestroy(): void {
    this.releaseContentArea?.();
    this.projectSub?.unsubscribe();
    this.routerSub?.unsubscribe();
    this.editProjectSub?.unsubscribe();
    this.openCreateSub?.unsubscribe();
    this.refreshSub?.unsubscribe();
    this.settingsTriggerSub?.unsubscribe();
    document.body.style.overflow = '';
  }

  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (this.projectSwitcherOpen) {
      if (!target.closest('.project-selector') && !target.closest('.project-dropdown')) {
        this.projectSwitcherOpen = false;
      }
    }
    if (this.notificationsOpen) {
      if (!target.closest('.notif-bell') && !target.closest('.notif-side-panel')) {
        this.closeNotifications();
      }
    }
  }

  onDocumentEscape(): void {
    if (this.notificationsOpen) {
      this.closeNotifications();
    }
    if (this.projectSwitcherOpen) {
      this.projectSwitcherOpen = false;
    }
  }

  private updateActiveNavFromRoute(): void {
    const url = this.router.url;
    if (url.includes('/sites')) {
      this.activeNav = 'sites';
    } else if (url.includes('/requests')) {
      this.activeNav = 'requests';
    } else if (url.includes('/members')) {
      this.activeNav = 'members';
    } else {
      this.activeNav = 'dashboard';
    }
  }

  private static readonly NAV_TITLES: Record<'dashboard' | 'sites' | 'requests' | 'members', string> = {
    dashboard: 'Dashboard',
    sites: 'Sites Updates',
    requests: 'Requests',
    members: 'Members',
  };

  /** Header title for the current section — mirrors the sidebar's active nav item. */
  public get pageTitle(): string {
    return ProjectDashboardLayoutComponent.NAV_TITLES[this.activeNav];
  }

  /** Whether the current user belongs to at least one project in this org. */
  public get hasProjects(): boolean {
    return this.projects.length > 0;
  }

  /** True once we know for sure there are 0 projects — drives the locked nav visuals. */
  public get navLocked(): boolean {
    return this.projectsLoaded && !this.hasProjects;
  }

  /** On Sites/Requests, a multi-project org gets the navbar project selector instead of a plain title. */
  public get showProjectSelector(): boolean {
    return (this.activeNav === 'sites' || this.activeNav === 'requests') && this.projects.length > 1;
  }

  public setActiveNav(nav: 'dashboard' | 'sites' | 'requests' | 'members' | 'settings'): void {
    this.hideNavTooltip();
    if (nav !== 'dashboard' && nav !== 'settings' && this.navLocked) {
      return;
    }
    if (nav === 'settings') {
      this.openSettingsModal();
      return;
    }
    this.activeNav = nav;
    const routeMap: Record<Exclude<typeof nav, 'settings'>, string> = {
      dashboard: 'dashboard',
      sites: 'sites',
      requests: 'requests',
      members: 'members',
    };
    this.router.navigate([routeMap[nav]], { relativeTo: this.route });
    this.closeMobileNav();
  }

  public openSettingsModal(): void {
    this.settingsModalOpen = true;
    this.closeMobileNav();
  }

  public closeSettingsModal(): void {
    this.settingsModalOpen = false;
    this.pendingSettingsSection = null;
    this.pendingSettingsProjectId = undefined;
    this.pendingSettingsIntegration = null;
    this.pendingSettingsWorkspaceLinkId = undefined;
  }

  /**
   * What to tell someone who came back from an install screen without an installation, or
   * `null` when there is nothing to tell: HubSpot's `already_connecting` means another tab's
   * install is probably done, and the statuses the modal loads on opening will show it.
   */
  private oauthReturnErrorMessage(integration: 'slack' | 'hubspot' | undefined, error: string): string | null {
    if (integration === 'hubspot') {
      switch (error) {
        case 'already_connecting':
          return null;
        case 'invalid_state':
        case 'state_expired':
          return 'That HubSpot link expired. Connect again.';
        case 'organization_deleted':
          return 'This organization no longer exists.';
        case 'token_exchange_failed':
        case 'missing_params':
        case 'unknown':
          return "HubSpot couldn't be connected. Try again.";
        default:
          // Any other slug is HubSpot's own `error`, `access_denied` among them: the person
          // backed out of HubSpot's screen.
          return "The HubSpot install was cancelled. Connect again when you're ready.";
      }
    }
    return error === 'access_denied'
      ? 'The Slack install was cancelled, so nothing was connected.'
      : 'Slack could not be connected. Please try again.';
  }

  public onCreateProject(): void {
    this.editingProject = null;
    this.createPanelOpen = true;
  }

  public onEditProject(project: EditProjectData): void {
    this.editingProject = project;
    this.createPanelOpen = true;
  }

  public onCloseCreatePanel(): void {
    this.createPanelOpen = false;
    this.editingProject = null;
  }

  public onProjectCreated(project: ProjectData): void {
    const payload: ProjectRequestDto = {
      name: project.name,
      description: project.description,
      color: project.color,
      organization: this.session.organizationId(),
    };
    this.projectApiService.createProject(payload).subscribe({
      next: () => {
        this.dashboardService.triggerRefresh();
        this.createPanelOpen = false;
        this.editingProject = null;
      },
      error: (error) => {
        const err = this.errorHandler.getUserFriendlyErrorMessage(error);
        console.error('Error creating project:', err);
        // Panel stays open on error so the user can retry.
      },
    });
  }

  public onProjectUpdated(project: ProjectData): void {
    const payload: ProjectRequestDto = {
      name: project.name,
      description: project.description,
      color: project.color,
      organization: this.session.organizationId(),
    };
    this.projectApiService.updateProject(payload, project.id).subscribe({
      next: () => {
        this.dashboardService.triggerRefresh();
        this.createPanelOpen = false;
        this.editingProject = null;
      },
      error: (error) => {
        const err = this.errorHandler.getUserFriendlyErrorMessage(error);
        console.error('Error updating project:', err);
      },
    });
  }

  /**
   * Fired after the settings modal deletes or leaves a project. Always send the user back
   * to the dashboard and hard-reload rather than patching state in place — the deleted
   * project's id can be cached in several independent places (the active ProjectService
   * selection, the Requests/Sites multi-project selector's own checked-set + localStorage,
   * merged kanban/sites views), and a reload re-derives all of it from a fresh fetch instead
   * of chasing down every consumer individually.
   */
  public onProjectRemoved(_projectId: string): void {
    // Small delay so the settings modal's success toast is visible before navigation wipes it.
    setTimeout(() => {
      window.location.href = '/dashboard';
    }, 400);
  }

  public toggleSidebar(): void {
    this.sidebarStateService.toggle();
    // Dismiss any lingering collapsed-nav-item tooltip rather than tracking its
    // position reactively through the width transition.
    this.hideNavTooltip();
  }

  /** Shows a nav item's tooltip — only relevant once the sidebar is collapsed to icon-only. */
  public showNavTooltip(event: MouseEvent, text: string): void {
    if (!this.sidebarCollapsed) return;
    this.setNavTooltip(event, text);
  }

  public hideNavTooltip(): void {
    this.navTooltip.set(null);
  }

  private setNavTooltip(event: MouseEvent, text: string): void {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.navTooltip.set({ text, top: rect.top + rect.height / 2, left: rect.right + 8 });
  }

  public toggleProjectSwitcher(event: Event): void {
    event.stopPropagation();
    this.projectSwitcherOpen = !this.projectSwitcherOpen;
  }

  public switchProject(project: ProjectsDataDto, event: Event): void {
    event.stopPropagation();
    this.selectProject(project);
    this.projectSwitcherOpen = false;
    this.router.navigate(['/dashboard']);
  }

  private selectProject(project: ProjectsDataDto): void {
    this.projectService.setProject({
      id: project.id,
      name: project.name,
      description: project.description,
      color: project.color,
      logo: project.logo,
    });
  }

  public toggleMobileNav(): void {
    this.mobileNavOpen = !this.mobileNavOpen;
    document.body.style.overflow = this.mobileNavOpen ? 'hidden' : '';
  }

  public closeMobileNav(): void {
    if (this.mobileNavOpen) {
      this.mobileNavOpen = false;
      document.body.style.overflow = '';
    }
  }

  public toggleNotifications(): void {
    this.notificationsOpen = !this.notificationsOpen;
    if (this.notificationsOpen) {
      this.notificationService.loadNotificationsList();
    }
  }

  public closeNotifications(): void {
    this.notificationsOpen = false;
  }
}
